import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from './Icon';
import HelpIcon from './help/HelpIcon';
import { testMercadoPagoToken } from '../services/api';
import {
  toInheritShape,
  hasOrgDefaults,
  overrideMethodConfig,
  inheritedMethodConfig,
} from './paymentMethods';
import './PaymentMethodsForm.css';

const maskKey = (key) => {
  if (!key || key.length <= 12) return key || '';
  return key.slice(0, 8) + '••••••••' + key.slice(-4);
};

const PaymentMethodsForm = ({ paymentMethods, onChange, orgDefaults = null }) => {
  const { t } = useTranslation();
  const [mpTestStatus, setMpTestStatus] = useState(null); // null | 'testing' | 'success' | 'error'
  const [mpTestMessage, setMpTestMessage] = useState('');
  const [editingField, setEditingField] = useState(null);
  // Credenciales write-only: rastrea que campos secretos editó el usuario en esta sesión.
  // Si no los tocó, el input se muestra vacío con placeholder "configurado" y el valor real
  // (cargado del backend) se conserva en state para round-trip sin revelarlo en pantalla.
  const [editedSecrets, setEditedSecrets] = useState({});

  // Shape de herencia (NER-1285). Retrocompat: un bag plano legacy entra como
  // `inherit:false` con overrides = el objeto plano. Siempre emitimos
  // `{ inherit, overrides }`, que es lo que valida el backend (NER-1275).
  const { inherit, overrides } = toInheritShape(paymentMethods);
  const orgDefaultsAvailable = hasOrgDefaults(orgDefaults);

  // Un método está heredado (sólo lectura) si el toggle global está activo, hay
  // defaults org-level y la sucursal NO tiene un override propio para ese método.
  const isInherited = (method) => inherit && orgDefaultsAvailable && !overrides[method];

  // Config a mostrar: heredada (defaults org, sin credenciales) o el override.
  const cfgFor = (method) =>
    isInherited(method)
      ? inheritedMethodConfig(orgDefaults, method)
      : overrideMethodConfig(overrides, method);

  const emit = (nextInherit, nextOverrides) =>
    onChange({ inherit: nextInherit, overrides: nextOverrides });

  const setInherit = (value) => {
    emit(value, overrides);
    setMpTestStatus(null);
  };

  // Escribe un campo del override del restaurante (sólo en modo editable).
  const handleChange = (method, field, value) => {
    emit(inherit, {
      ...overrides,
      [method]: { ...overrideMethodConfig(overrides, method), [field]: value },
    });
  };

  const handleToggle = (method) => {
    handleChange(method, 'enabled', !overrideMethodConfig(overrides, method).enabled);
  };

  // Marca un campo secreto como editado y propaga el nuevo valor.
  const handleSecretChange = (method, field, value) => {
    const key = `${method}.${field}`;
    if (!editedSecrets[key]) setEditedSecrets((prev) => ({ ...prev, [key]: true }));
    handleChange(method, field, value);
  };

  const isSecretEdited = (method, field) => !!editedSecrets[`${method}.${field}`];

  // Overridear para esta sucursal: siembra el override desde el default org-level
  // (sólo campos heredables, sin credenciales) y pasa el método a editable.
  const overrideForBranch = (method) => {
    emit(inherit, { ...overrides, [method]: inheritedMethodConfig(orgDefaults, method) });
  };

  // Volver a heredar: elimina el override del método; vuelve a leer los defaults org.
  const revertToInherited = (method) => {
    const next = { ...overrides };
    delete next[method];
    emit(inherit, next);
    setMpTestStatus(null);
  };

  const handleTestMercadoPago = async () => {
    const token = cfgFor('mercadoPago').accessToken;
    if (!token) return;
    setMpTestStatus('testing');
    setMpTestMessage('');
    try {
      await testMercadoPagoToken(token);
      setMpTestStatus('success');
      setMpTestMessage(t('payment.connectionSuccess'));
    } catch (err) {
      setMpTestStatus('error');
      setMpTestMessage(err?.response?.data?.message || t('payment.connectionError'));
    }
  };

  const cash = cfgFor('cash');
  const card = cfgFor('card');
  const transfer = cfgFor('transfer');
  const mercadoPago = cfgFor('mercadoPago');
  const lineCheckout = cfgFor('lineCheckout');
  const mpIsConnected = !!mercadoPago.accessToken;

  // Footer de acciones de herencia por método (override / volver a heredar).
  const inheritActions = (method) => {
    if (!inherit || !orgDefaultsAvailable) return null;
    if (isInherited(method)) {
      return (
        <div className="method-inherit-actions">
          <span className="inherited-note">
            <Icon name="account_tree" size="small" />
            {t('payment.inherit.inheritedNote')}
          </span>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={() => overrideForBranch(method)}
          >
            {t('payment.inherit.overrideForBranch')}
          </button>
        </div>
      );
    }
    return (
      <div className="method-inherit-actions">
        <span className="overridden-note">
          <Icon name="edit" size="small" />
          {t('payment.inherit.overriddenNote')}
        </span>
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          onClick={() => revertToInherited(method)}
        >
          {t('payment.inherit.revertToInherited')}
        </button>
      </div>
    );
  };

  return (
    <div className="payment-methods-form">
      <h3>
        {t('restaurant.paymentMethods')} <HelpIcon topicKey="paymentMethods.scope" />
      </h3>

      {/* Toggle de herencia org-level (NER-1285) */}
      {orgDefaultsAvailable ? (
        <div className="payment-inherit-banner">
          <div className="inherit-info">
            <Icon name="account_tree" />
            <div>
              <span className="inherit-title">
                {t('payment.inherit.title')} <HelpIcon topicKey="paymentMethods.inherit" />
              </span>
              <p className="inherit-desc">{t('payment.inherit.description')}</p>
            </div>
          </div>
          <label className="switch">
            <input
              type="checkbox"
              checked={inherit}
              aria-label={t('payment.inherit.title')}
              onChange={(e) => setInherit(e.target.checked)}
            />
            <span className="slider round"></span>
          </label>
        </div>
      ) : (
        <p className="payment-inherit-hint">
          <Icon name="info" size="small" />
          {t('payment.inherit.noDefaults')}
        </p>
      )}

      {/* Efectivo */}
      <div className={`payment-method-card ${isInherited('cash') ? 'is-inherited' : ''}`}>
        <div className="method-header">
          <div className="method-title">
            <Icon name="payments" />
            <span>{t('payment.cash')}</span>
          </div>
          <label className="switch">
            <input
              type="checkbox"
              checked={cash.enabled}
              disabled={isInherited('cash')}
              onChange={() => handleToggle('cash')}
            />
            <span className="slider round"></span>
          </label>
        </div>
        {inheritActions('cash')}
      </div>

      {/* Tarjeta */}
      <div className={`payment-method-card ${isInherited('card') ? 'is-inherited' : ''}`}>
        <div className="method-header">
          <div className="method-title">
            <Icon name="credit_card" />
            <span>{t('payment.card')}</span>
          </div>
          <label className="switch">
            <input
              type="checkbox"
              checked={card.enabled}
              disabled={isInherited('card')}
              onChange={() => handleToggle('card')}
            />
            <span className="slider round"></span>
          </label>
        </div>
        {inheritActions('card')}
      </div>

      {/* Transferencia */}
      <div className={`payment-method-card ${isInherited('transfer') ? 'is-inherited' : ''}`}>
        <div className="method-header">
          <div className="method-title">
            <Icon name="account_balance" />
            <span>{t('payment.transfer')}</span>
          </div>
          <label className="switch">
            <input
              type="checkbox"
              checked={transfer.enabled}
              disabled={isInherited('transfer')}
              onChange={() => handleToggle('transfer')}
            />
            <span className="slider round"></span>
          </label>
        </div>

        {transfer.enabled && (
          <div className="method-details">
            <div className="form-group">
              <label>{t('payment.bank')}</label>
              <input
                type="text"
                value={transfer.bank || ''}
                disabled={isInherited('transfer')}
                onChange={(e) => handleChange('transfer', 'bank', e.target.value)}
                placeholder="Ej: Banco Galicia"
              />
            </div>
            <div className="form-group">
              <label>{t('payment.cbu')}</label>
              <input
                type="text"
                value={transfer.cbu || ''}
                disabled={isInherited('transfer')}
                onChange={(e) => handleChange('transfer', 'cbu', e.target.value)}
                placeholder="0000000000000000000000"
              />
            </div>
            <div className="form-group">
              <label>{t('payment.alias')}</label>
              <input
                type="text"
                value={transfer.alias || ''}
                disabled={isInherited('transfer')}
                onChange={(e) => handleChange('transfer', 'alias', e.target.value)}
                placeholder="Ej: MI.ALIAS.MP"
              />
            </div>
          </div>
        )}
        {inheritActions('transfer')}
      </div>

      {/* Mercado Pago */}
      <div className={`payment-method-card ${isInherited('mercadoPago') ? 'is-inherited' : ''}`}>
        <div className="method-header">
          <div className="method-title">
            <Icon name="qr_code_2" />
            <span>{t('payment.mercadopago')}</span>
            {mercadoPago.enabled && !isInherited('mercadoPago') && (
              <span className={`connection-badge ${mpIsConnected ? 'connected' : 'disconnected'}`}>
                {mpIsConnected ? t('payment.connected') : t('payment.disconnected')}
              </span>
            )}
          </div>
          <label className="switch">
            <input
              type="checkbox"
              checked={mercadoPago.enabled}
              disabled={isInherited('mercadoPago')}
              onChange={() => handleToggle('mercadoPago')}
            />
            <span className="slider round"></span>
          </label>
        </div>

        {mercadoPago.enabled && isInherited('mercadoPago') && (
          <div className="method-details">
            <p className="method-description inherited-credentials-note">
              {t('payment.inherit.credentialsPerBranch')}
            </p>
          </div>
        )}

        {mercadoPago.enabled && !isInherited('mercadoPago') && (
          <div className="method-details">
            <p className="method-description">{t('payment.mpDesc')}</p>
            <div className="form-group">
              <label>{t('payment.publicKey')}</label>
              <input
                type="text"
                value={
                  editingField === 'mp-publicKey'
                    ? mercadoPago.publicKey || ''
                    : maskKey(mercadoPago.publicKey)
                }
                onFocus={() => setEditingField('mp-publicKey')}
                onBlur={() => setEditingField(null)}
                onChange={(e) => handleChange('mercadoPago', 'publicKey', e.target.value)}
                placeholder="TEST-..."
              />
              {mercadoPago.publicKey?.startsWith('TEST') && (
                <small className="key-warning">{t('payment.sandboxWarning')}</small>
              )}
            </div>
            <div className="form-group">
              <label>{t('payment.accessToken')}</label>
              <div className="input-with-action">
                <input
                  type="password"
                  value={
                    isSecretEdited('mercadoPago', 'accessToken')
                      ? mercadoPago.accessToken || ''
                      : ''
                  }
                  onChange={(e) => {
                    handleSecretChange('mercadoPago', 'accessToken', e.target.value);
                    setMpTestStatus(null);
                  }}
                  placeholder={
                    mpIsConnected && !isSecretEdited('mercadoPago', 'accessToken')
                      ? t('payment.credentialConfigured')
                      : 'TEST-...'
                  }
                  autoComplete="off"
                />
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={handleTestMercadoPago}
                  disabled={!mercadoPago.accessToken || mpTestStatus === 'testing'}
                >
                  {mpTestStatus === 'testing'
                    ? t('payment.testingConnection')
                    : t('payment.testConnection')}
                </button>
              </div>
              {mpTestStatus === 'success' && (
                <p className="connection-feedback success">{mpTestMessage}</p>
              )}
              {mpTestStatus === 'error' && (
                <p className="connection-feedback error">{mpTestMessage}</p>
              )}
            </div>
          </div>
        )}
        {inheritActions('mercadoPago')}
      </div>

      {/* Line Checkout (MODO) */}
      <div className={`payment-method-card ${isInherited('lineCheckout') ? 'is-inherited' : ''}`}>
        <div className="method-header">
          <div className="method-title">
            <Icon name="smartphone" />
            <span>{t('payment.lineCheckout')}</span>
          </div>
          <label className="switch">
            <input
              type="checkbox"
              checked={lineCheckout.enabled}
              disabled={isInherited('lineCheckout')}
              onChange={() => handleToggle('lineCheckout')}
            />
            <span className="slider round"></span>
          </label>
        </div>

        {lineCheckout.enabled && isInherited('lineCheckout') && (
          <div className="method-details">
            <p className="method-description inherited-credentials-note">
              {t('payment.inherit.credentialsPerBranch')}
            </p>
          </div>
        )}

        {lineCheckout.enabled && !isInherited('lineCheckout') && (
          <div className="method-details">
            <p className="method-description">{t('payment.lineCheckoutDesc')}</p>
            <div className="form-group">
              <label>{t('payment.linePublicKey')}</label>
              <input
                type="text"
                value={
                  editingField === 'lc-publicKey'
                    ? lineCheckout.publicKey || ''
                    : maskKey(lineCheckout.publicKey)
                }
                onFocus={() => setEditingField('lc-publicKey')}
                onBlur={() => setEditingField(null)}
                onChange={(e) => handleChange('lineCheckout', 'publicKey', e.target.value)}
                placeholder="pk_modo_..."
              />
              {lineCheckout.publicKey?.includes('sandbox') && (
                <small className="key-warning">{t('payment.sandboxWarning')}</small>
              )}
            </div>
            <div className="form-group">
              <label>{t('payment.lineSecretKey')}</label>
              <input
                type="password"
                value={
                  isSecretEdited('lineCheckout', 'secretKey') ? lineCheckout.secretKey || '' : ''
                }
                onChange={(e) => handleSecretChange('lineCheckout', 'secretKey', e.target.value)}
                placeholder={
                  lineCheckout.secretKey && !isSecretEdited('lineCheckout', 'secretKey')
                    ? t('payment.credentialConfigured')
                    : 'sk_modo_...'
                }
                autoComplete="off"
              />
            </div>
          </div>
        )}
        {inheritActions('lineCheckout')}
      </div>
    </div>
  );
};

export default PaymentMethodsForm;
